import { EmberChapterChecklist } from "game-common/models"
import { BLEND_MODES, Container, Graphics, InteractionEvent, InteractionManager, LINE_JOIN, Point, Text } from "pixi.js"
import { getRendererApp } from "../renderer_app"
import { TextRenderer } from "../text/text_renderer"
import { chamfer, FACE, groove, INK, RIM, RIM_LIGHT, rivet, TIMBER, TIMBER_EDGE } from "./build_board"
import {
    diamond,
    filledSocket,
    glowDisc,
    REWARD_EMBER,
    REWARD_GOLD,
    REWARD_GOLD_HOT,
    REWARD_INK_DARK,
    REWARD_WHITE,
    shineBand,
    spark,
    sparkBurst,
} from "./chapter_reward_art"
import {
    ctaPose,
    currentPulse,
    Eased,
    easeInCubic,
    easeOutBack,
    easeOutCubic,
    lerp,
    lerpColor,
    span,
    stampPose,
} from "./chapter_reward_motion"
import { UiButton } from "./ui_button"
import { getUiMetrics } from "./ui_metrics"

// The chapter's task list: the player's quest log, under the lord's fire gauge.
//
// It used to be a translucent rounded card of square checkboxes with a "Chapter 2 · 1/2" counter --
// a to-do app's furniture, and the camp's one rewarding moment (a task done) was a box quietly
// gaining a tick in the corner of the screen. Now it is build_board's cut timber with gold in it:
//
//   * every task sits in a DIAMOND socket; the one to do next glows softly -- a warm band across its
//     row and an ember breathing in its socket -- so the eye lands on what to do; later ones wait dim
//   * the header carries the chapter as a gold overline, its title, and one pip per task
//   * a task finishing is a beat, not a state change (see chapter_celebration.ts): a toast pops
//     mid-screen, its diamond flies here, and only when it LANDS does the row stamp -- the socket
//     slams in gold, the check draws itself, sparks and a ring leave it, light crosses the row
//   * a chapter finishing ends in the fanfare, which flies into this panel and leaves the choice of
//     what next -- Continue opens the next chapter preview -- lighting up at its foot. That button used to
//     be a separate chip parked at a fixed point near the bottom of the screen.
//
// NOTHING SNAPS. Every state the panel can be in is reached by a timed ease (Eased): the objective's
// glow hands over from the row just finished to the next one, the button lights up from the left
// rather than appearing, rows cascade in and fade out on expand/collapse while the board folds to its
// new height, and the header warms to gold when the last task lands.
//
// Static content is rebuilt only on a change (layout); the glows, pips, stamps and transitions are
// repainted per frame by animate(), off GuiManager.updateTimer.

export type ChapterTask = EmberChapterChecklist["tasks"][number]

export type ChapterCallToAction =
    | { kind: "continue"; body: string; label: string }
    | { kind: "next"; chapter: number; title: string }

export interface ChapterChecklistEvents {
    /** A task finished while the player watched. Its row stays unchecked until reveal(task.id). */
    onTaskCompleted?: (task: ChapterTask, chapter: number) => void
    /** The chapter's completion was witnessed live. The call to action waits for dock(). */
    onChapterCompleted?: (chapter: number, title: string) => void
    onContinue?: () => void
    onNextChapter?: (chapter: number) => void
}

const WIDTH = 228
const HEADER_HEIGHT = 46
const CONTENT_X = 14
const SOCKET_X = 22
const SOCKET_R = 5.5
const LABEL_X = 36
const ROW_MIN_H = 20
const PIP_R = 3.4
const PIP_STEP = 10
const TOGGLE_W = 20
const BUTTON_H = 30

const DONE_TEXT = 0xc9a86a
const TODO_TEXT = 0x93826b
const CURRENT_TEXT = INK
const CURRENT_WARM = 0xffd99a
const SOCKET_EMPTY = 0x0d0a07
const SOCKET_RIM_TODO = 0x4a3f30
const UNLIT_FILL = 0x2a201a
const UNLIT_RIM = 0x6b4f38

// Transition lengths.
const ROW_HANDOFF_MS = 520
const FOLD_MS = 300
const ROW_IN_MS = 260
const ROW_IN_STAGGER = 45
const ROW_OUT_MS = 170
const ROW_OUT_STAGGER = 28
const HEADER_GOLD_MS = 650
const CONTENT_FADE_MS = 260
// The folded bar a finished chapter shrinks to: just its button and the toggle to open it again.
const COMPACT_PAD = 8
const HOVER_MS = 160
// A panel appearing unfolds from a sliver of board rather than popping in whole.
const UNFOLD_FROM = 18

// Celebrations that never arrive -- a hidden tab, a lost frame loop -- must not leave a task
// unchecked or the next chapter unreachable.
const PENDING_REVEAL_LIMIT_MS = 9000
const DOCK_LIMIT_MS = 12000
// The server's "chapter complete" and the list's last tick are separate messages and can land in
// either order; a completion only earns the fanfare when the two arrive this close together.
const COMPLETION_WINDOW_MS = 20000
// A "continue" arriving before the list is finished waits this long for the last tick to land, so
// the button does not appear first and then vanish under the fanfare.
const CONTINUE_HOLD_MS = 3000
// After Continue the server takes its prompt down and sends the next-chapter one a moment
// later. The spent button stays until it arrives rather than the panel collapsing and regrowing.
const RETIRE_MS = 1600

type RowState = "done" | "current" | "todo"
type Mode = "full" | "folded" | "compact"

interface Row {
    task: ChapterTask
    index: number
    socket: Graphics
    label: Text
    top: number
    height: number
    state: RowState
}

/** Per task, across relayouts: its objective glow and its label colour, each easing to its state. */
interface RowLook {
    glow: Eased
    tint: Eased
}

const rowTint = (state: RowState) => (state === "done" ? DONE_TEXT : state === "current" ? CURRENT_TEXT : TODO_TEXT)

/**
 * Every task on the list done, and none still to be dealt onto it: a chapter that hands its tasks out
 * one at a time can have every listed task done while it waits to give the next (`more`).
 */
const allDone = (data: EmberChapterChecklist) => data.tasks.every(task => task.complete) && !data.more

export class ChapterChecklist extends Container {
    private data?: EmberChapterChecklist
    private minimized = true
    private hovered = false
    private panelHeight = HEADER_HEIGHT
    private toggleButton?: UiButton
    private toggleGlyph = new Graphics()
    private toggleTurn = new Eased(1, 240)
    private headerHandle?: Graphics
    private manualPosition?: Point
    private drag?: { id: number; offset: Point; interaction?: InteractionManager }

    private board = new Graphics()
    private under = new Graphics()
    private content = new Container()
    private contentMask = new Graphics()
    private pips = new Graphics()
    private over = new Graphics()
    private rows: Row[] = []
    private looks = new Map<string, RowLook>()
    private headerBottom = HEADER_HEIGHT
    private overline?: Text
    private title?: Text
    private chip?: Text
    private chipBox?: { x: number; w: number }

    private clock = 0
    private boardHeight = new Eased(0, FOLD_MS)
    private paintedHeight = -1
    private rowsInAt?: number
    private collapsingAt?: number
    private headerGold = new Eased(0, HEADER_GOLD_MS)
    private completeAt?: number
    private mode?: Mode
    private contentInAt?: number

    /** Finished on the server, waiting for their diamond to land: id -> when they finished. */
    private pending = new Map<string, number>()
    /** Stamps playing: id -> when the diamond landed. */
    private stamps = new Map<string, number>()

    private cta?: ChapterCallToAction
    private ctaShownAt = 0
    private ctaSpent = false
    private ctaSpentAt = 0
    private ctaRetiringAt?: number
    private ctaHeldUntil?: number
    private ctaLayer?: Container
    private ctaCover = new Graphics()
    private ctaButton?: UiButton
    private ctaBox?: { x: number; y: number; w: number; h: number }
    private ctaTop?: number
    private ctaSlide = new Eased(0, FOLD_MS)
    private ctaHover = new Eased(0, HOVER_MS)
    private ctaPressedAt?: number

    private liveCompleteAt?: number
    private continueAt?: number
    private fanfaredChapter?: number
    private awaitingDockSince?: number
    private dockFlashAt?: number

    constructor(
        private hover: (over: boolean) => void,
        private anchor: () => { x: number; y: number } | undefined = () => undefined,
        private events: ChapterChecklistEvents = {},
    ) {
        super()
        this.visible = false
        this.interactive = true
        this.under.blendMode = BLEND_MODES.ADD
        this.over.blendMode = BLEND_MODES.ADD
        this.addChild(this.board, this.under, this.pips, this.content, this.contentMask, this.over)
        this.on("mouseover", () => {
            this.hovered = true
            this.hover(true)
        })
        this.on("mouseout", () => {
            if (!this.drag) {
                this.hovered = false
                this.hover(false)
            }
        })
        this.on("pointerdown", event => event.stopPropagation())
    }

    setEvents = (events: ChapterChecklistEvents) => {
        this.events = events
    }

    update = (data: EmberChapterChecklist | null) => {
        const previous = this.data
        const live = !!previous && !!data && previous.chapter === data.chapter
        if (!data || previous?.chapter !== data.chapter) {
            // A new chapter (or none): nothing in flight belongs to it, and the list unfolds afresh.
            this.pending.clear()
            this.stamps.clear()
            this.looks.clear()
            this.liveCompleteAt = undefined
            this.awaitingDockSince = undefined
            this.completeAt = undefined
            this.collapsingAt = undefined
            if (data && previous) this.continueAt = undefined
            if (data) {
                this.rowsInAt = this.clock
                this.boardHeight.snap(this.visible ? this.boardHeight.value(this.clock) : UNFOLD_FROM)
                this.headerGold.snap(allDone(data) ? 1 : 0)
            }
        }
        this.data = data || undefined
        if (data && live) {
            const was = new Map(previous.tasks.map(task => [task.id, task.complete]))
            const finished = data.tasks.filter(task => task.complete && was.get(task.id) === false)
            finished.forEach(task => {
                if (this.events.onTaskCompleted) {
                    this.pending.set(task.id, this.clock)
                    this.events.onTaskCompleted(task, data.chapter)
                } else {
                    this.stamps.set(task.id, this.clock)
                }
            })
            if (finished.length && allDone(data)) {
                this.liveCompleteAt = this.clock
                this.maybeCelebrateChapter()
            }
        }
        this.refreshVisibility()
        this.layout()
        this.place()
    }

    /** A dropped celebration must still check the box; reveal is idempotent. */
    reveal = (taskId: string) => {
        if (!this.pending.delete(taskId)) return
        this.stamps.set(taskId, this.clock)
        this.layout()
    }

    /** The fanfare has flown into the panel: light it, and put the choice of what next on it. */
    dock = () => {
        if (this.awaitingDockSince === undefined) return
        this.awaitingDockSince = undefined
        this.dockFlashAt = this.clock
        this.ctaShownAt = this.clock
        this.layout()
    }

    /**
     * The server's chapter prompts, routed here rather than to the bottom-of-screen board.
     * `null` retracts a "continue" (the server's way of taking its completion prompt down) but leaves
     * a "next" standing, which is how the old chip behaved.
     */
    setCallToAction = (cta: ChapterCallToAction | null) => {
        if (!cta) {
            if (this.cta?.kind !== "continue") return
            if (this.ctaSpent) {
                this.ctaRetiringAt = this.clock
                return
            }
            this.cta = undefined
            this.continueAt = undefined
            this.refreshVisibility()
            this.layout()
            return
        }
        if (this.cta && JSON.stringify(this.cta) === JSON.stringify(cta) && !this.ctaSpent) return
        this.cta = cta
        this.ctaSpent = false
        this.ctaRetiringAt = undefined
        this.ctaShownAt = this.clock
        this.ctaHeldUntil = undefined
        this.ctaPressedAt = undefined
        this.ctaTop = undefined
        if (cta.kind === "continue") {
            this.continueAt = this.clock
            const finished = !!this.data && allDone(this.data)
            if (!finished && this.data) this.ctaHeldUntil = this.clock + CONTINUE_HOLD_MS
            this.maybeCelebrateChapter()
        }
        if (!this.visible) this.boardHeight.snap(UNFOLD_FROM)
        this.refreshVisibility()
        this.layout()
        this.place()
    }

    /** The next-chapter announcement is up: the button that opened it goes with it. */
    clearNextChapter = () => {
        if (this.cta?.kind !== "next") return
        this.cta = undefined
        this.refreshVisibility()
        this.layout()
    }

    hasCallToAction = () => !!this.cta
    hasTasks = () => !!this.data

    /** Where a task's diamond lands, in global space: its socket, or its pip while folded. */
    socketGlobal = (taskId: string): Point | undefined => {
        if (!this.visible || !this.data) return undefined
        const row = this.rows.find(entry => entry.task.id === taskId)
        if (row && !this.minimized) return this.toGlobal(new Point(SOCKET_X, row.top + row.height / 2))
        const index = this.data.tasks.findIndex(task => task.id === taskId)
        if (index < 0) return this.toGlobal(new Point(WIDTH / 2, this.headerBottom / 2))
        return this.toGlobal(this.pipCenter(index))
    }

    /** Where the fanfare docks: the button it leaves behind, or the panel's middle. */
    panelGlobal = (): Point | undefined => {
        if (!this.visible) return undefined
        const box = this.liveCtaBox()
        return this.toGlobal(new Point(WIDTH / 2, box ? box.y + box.h / 2 : this.panelHeight / 2))
    }

    private maybeCelebrateChapter = () => {
        if (!this.data || this.liveCompleteAt === undefined || this.continueAt === undefined) return
        if (this.fanfaredChapter === this.data.chapter) return
        if (Math.abs(this.liveCompleteAt - this.continueAt) > COMPLETION_WINDOW_MS) return
        this.fanfaredChapter = this.data.chapter
        this.ctaHeldUntil = undefined
        if (!this.events.onChapterCompleted) return
        this.awaitingDockSince = this.clock
        this.events.onChapterCompleted(this.data.chapter, this.data.title)
    }

    private refreshVisibility = () => {
        this.visible = !!this.data || !!this.cta
        if (!this.visible) {
            this.endDrag()
            if (this.hovered) {
                this.hovered = false
                this.hover(false)
            }
        }
    }

    private ctaVisible = () =>
        !!this.cta &&
        this.awaitingDockSince === undefined &&
        !(this.ctaHeldUntil !== undefined && this.clock < this.ctaHeldUntil)

    private complete = () => !!this.data && allDone(this.data) && this.pending.size === 0

    private shown = (task: ChapterTask) => task.complete && !this.pending.has(task.id)

    private pipCenter = (index: number) => {
        const count = this.data?.tasks.length || 0
        const right = WIDTH - CONTENT_X - TOGGLE_W - 8
        return new Point(right - (count - 1 - index) * PIP_STEP, 15)
    }

    private text = (
        value: string,
        color: number,
        size: number,
        bold = false,
        wrap?: number,
        spacing = 0,
        parent: Container = this.content,
    ) => {
        const renderer = new TextRenderer().fontSize(size).bold(bold).color(color).strokeThickness(0)
        if (wrap) renderer.wordWrap(wrap)
        const text = renderer.render(value) as Text
        if (spacing) text.style.letterSpacing = spacing
        parent.addChild(text)
        return text
    }

    private toggle = () => {
        if (this.collapsingAt !== undefined) return
        if (this.minimized) {
            this.minimized = false
            this.rowsInAt = this.clock
            this.layout()
            this.place()
        } else if (this.rows.length || this.foldsToCompact()) {
            // Fade out first; the fold itself happens when they have gone (settleClocks).
            this.collapsingAt = this.clock
        } else {
            this.minimized = true
            this.layout()
        }
        this.toggleTurn.set(this.minimized || this.collapsingAt !== undefined ? 1 : 0, this.clock)
    }

    /** Folding a list with a choice waiting at its foot leaves only that choice (and the way back). */
    private foldsToCompact = () => !!this.cta && this.ctaVisible()

    private addToggle = (x: number, y: number) => {
        // A drawn chevron that turns, rather than a "−" that swaps for a "+".
        this.toggleButton = new UiButton({
            label: "",
            size: "small",
            width: TOGGLE_W,
            height: TOGGLE_W,
            theme: "ember",
            appearance: "subtle",
            onClick: this.toggle,
        })
        this.toggleButton.position.set(x, y)
        this.content.addChild(this.toggleButton)
        this.toggleGlyph.position.set(x + TOGGLE_W / 2, y + TOGGLE_W / 2)
        this.content.addChild(this.toggleGlyph)
    }

    private addHandle = (w: number, h: number) => {
        const handle = (this.headerHandle = new Graphics().beginFill(0xffffff, 0.001).drawRect(0, 0, w, h).endFill())
        handle.interactive = true
        handle.cursor = this.drag ? "grabbing" : "grab"
        handle
            .on("pointerdown", this.startDrag)
            .on("pointermove", this.moveDrag)
            .on("pointerup", this.endDrag)
            .on("pointerupoutside", this.endDrag)
            .on("pointercancel", this.endDrag)
        this.content.addChild(handle)
    }

    private layout = () => {
        // The persistent pieces are lifted out before the rebuild destroys everything else.
        for (const keep of [this.ctaCover, this.toggleGlyph]) keep.parent?.removeChild(keep)
        this.content.removeChildren().forEach(child => child.destroy({ children: true }))
        this.rows = []
        this.ctaButton = undefined
        this.ctaLayer = undefined
        this.ctaBox = undefined
        this.chipBox = undefined
        this.toggleButton = undefined
        this.headerHandle = undefined
        this.overline = this.title = this.chip = undefined
        const previousCtaTop = this.ctaTop
        this.ctaTop = undefined
        if (!this.visible) {
            this.board.clear()
            this.under.clear()
            this.over.clear()
            this.pips.clear()
            this.contentMask.clear()
            this.paintedHeight = -1
            return
        }
        const data = this.data
        const complete = this.complete()
        if (complete && data && this.completeAt === undefined) {
            this.completeAt = this.clock
            this.headerGold.set(1, this.clock)
        }
        const mode: Mode = this.minimized && this.foldsToCompact() ? "compact" : this.minimized ? "folded" : "full"
        // Into or out of the bar the whole face changes, so it fades in afresh rather than sliding.
        const recomposed =
            this.mode !== undefined && mode !== this.mode && (mode === "compact" || this.mode === "compact")
        if (recomposed) this.contentInAt = this.clock
        this.mode = mode
        let y = 0
        if (mode === "compact") {
            y = this.layoutCompact()
        } else if (data) {
            this.overline = this.text(`CHAPTER ${data.chapter}`, 0xffffff, 8, true, undefined, 1.6)
            this.overline.position.set(CONTENT_X + 12, 10)
            this.title = this.text(data.title, 0xffffff, 13, true, WIDTH - CONTENT_X * 2)
            this.title.position.set(CONTENT_X, 23)
            this.headerBottom = Math.max(HEADER_HEIGHT, Math.ceil(this.title.y + this.title.height + 8))
            if (complete) {
                // letterSpacing adds a trailing advance after the last glyph; take it back off.
                this.chip = this.text("COMPLETE", REWARD_INK_DARK, 7, true, undefined, 1.2)
                const w = Math.ceil(this.chip.width - 1.2) + 12
                this.chipBox = { x: WIDTH - CONTENT_X - TOGGLE_W - 6 - w, w }
                this.chip.anchor.set(0.5, 0.5)
                this.chip.position.set(this.chipBox.x + w / 2, 14.5)
            }
            this.addHandle(WIDTH - TOGGLE_W - 14, this.headerBottom)
            this.addToggle(WIDTH - CONTENT_X - TOGGLE_W + 4, 6)
            y = this.headerBottom
            // The folded checklist keeps the first unfinished objective in list order.
            // Live celebrations may hold a completed row unchecked in the full list, but
            // the folded preview moves on as soon as the server marks it complete.
            const current = data.tasks.find(task => (this.minimized ? !task.complete : !this.shown(task)))
            const tasks = this.minimized ? (current ? [current] : []) : data.tasks
            if (tasks.length) {
                y += 5
                tasks.forEach((task, index) => {
                    const state: RowState = this.shown(task) ? "done" : task === current ? "current" : "todo"
                    const look = this.lookFor(task.id, state)
                    look.glow.set(state === "current" ? 1 : 0, this.clock)
                    look.tint.set(rowTint(state), this.clock)
                    const label = this.text(task.label, 0xffffff, 11, false, WIDTH - LABEL_X - CONTENT_X)
                    const height = Math.max(ROW_MIN_H, Math.ceil(label.height + 7))
                    label.position.set(LABEL_X, y + (height - label.height) / 2)
                    const socket = new Graphics()
                    socket.position.set(SOCKET_X, y + height / 2)
                    this.content.addChild(socket)
                    const row = { task, index, socket, label, top: y, height, state }
                    this.rows.push(row)
                    this.paintSocket(row, 1)
                    y += height
                })
                y += 5
            }
        } else {
            this.headerBottom = 0
        }
        if (mode !== "compact" && this.cta && this.ctaVisible()) {
            y = this.layoutCallToAction(y, recomposed ? undefined : previousCtaTop)
        }
        this.panelHeight = mode === "compact" ? y : Math.max(y, data ? this.headerBottom : 0)
        this.boardHeight.set(this.panelHeight, this.clock)
        this.paintedHeight = -1
        this.animate(0)
    }

    private lookFor = (id: string, state: RowState) => {
        let look = this.looks.get(id)
        if (!look) {
            // A row seen for the first time starts in its state; only CHANGES are eased.
            look = {
                glow: new Eased(state === "current" ? 1 : 0, ROW_HANDOFF_MS),
                tint: new Eased(rowTint(state), ROW_HANDOFF_MS, lerpColor),
            }
            this.looks.set(id, look)
        }
        return look
    }

    private layoutCallToAction = (top: number, previousTop?: number) => {
        const cta = this.cta
        this.ctaTop = top
        // The section keeps its place on screen while the rows above it fold, then glides to its new one.
        if (previousTop !== undefined && previousTop !== top) {
            this.ctaSlide.snap(previousTop - top + this.ctaSlide.value(this.clock))
            this.ctaSlide.set(0, this.clock)
        }
        const layer = (this.ctaLayer = new Container())
        this.content.addChild(layer)
        let y = this.data ? 9 : 12
        if (cta.kind === "continue") {
            if (cta.body) {
                const body = this.text(cta.body, 0xd8c7ab, 10, false, WIDTH - CONTENT_X * 2 - 2, 0, layer)
                body.position.set(CONTENT_X + 1, y)
                y += Math.ceil(body.height) + 9
            }
        } else {
            const overline = this.text(`UP NEXT · CHAPTER ${cta.chapter}`, RIM_LIGHT, 8, true, undefined, 1.4, layer)
            overline.position.set(CONTENT_X + 1, y)
            y += 13
            const title = this.text(cta.title, INK, 12, true, WIDTH - CONTENT_X * 2, 0, layer)
            title.position.set(CONTENT_X + 1, y)
            y += Math.ceil(title.height) + 9
        }
        this.addCtaButton(layer, CONTENT_X, y, WIDTH - CONTENT_X * 2)
        return top + y + BUTTON_H + 12
    }

    /** The folded panel keeps the next chapter's details above its button. */
    private layoutCompact = () => {
        this.ctaTop = 0
        this.headerBottom = 0
        const layer = (this.ctaLayer = new Container())
        this.content.addChild(layer)
        let y = COMPACT_PAD
        if (this.cta.kind === "next") {
            const overline = this.text(
                `UP NEXT · CHAPTER ${this.cta.chapter}`,
                RIM_LIGHT,
                8,
                true,
                undefined,
                1.4,
                layer,
            )
            overline.position.set(CONTENT_X + 1, y)
            y += 13
            const title = this.text(this.cta.title, INK, 12, true, WIDTH - CONTENT_X * 2, 0, layer)
            title.position.set(CONTENT_X + 1, y)
            y += Math.ceil(title.height) + 9
        }
        const height = y + BUTTON_H + COMPACT_PAD
        this.addHandle(WIDTH, height)
        this.content.addChild(layer)
        const toggleX = WIDTH - 10 - TOGGLE_W
        this.addCtaButton(layer, 10, y, toggleX - 6 - 10)
        this.addToggle(toggleX, y + (BUTTON_H - TOGGLE_W) / 2)
        return height
    }

    // A timber plate with a gold rim, the tutorial board's own primary button: an invitation the
    // player can take whenever they are ready, not a lit sign urging them on.
    private addCtaButton = (layer: Container, x: number, y: number, w: number) => {
        const cta = this.cta
        const label = cta.kind === "continue" ? `${cta.label || "Continue"}  ›` : "Next Chapter  ›"
        const button = (this.ctaButton = new UiButton({
            label,
            width: w,
            height: BUTTON_H,
            size: "small",
            theme: "ember",
            shape: "chamfer",
            variant: "primary",
            accent: REWARD_GOLD,
            textColor: INK,
            bold: true,
            fontSize: 12,
            enabled: !this.ctaSpent,
            onClick: this.pressCallToAction,
        }))
        button.pivot.set(w / 2, BUTTON_H / 2)
        button.position.set(x + w / 2, y + BUTTON_H / 2)
        for (const event of ["pointerover", "mouseover"]) button.on(event, () => this.ctaHover.set(1, this.clock))
        for (const event of ["pointerout", "mouseout", "pointerupoutside", "pointercancel"]) {
            button.on(event, () => this.ctaHover.set(0, this.clock))
        }
        layer.addChild(button)
        // Over the button: the unlit plate the light crosses.
        layer.addChild(this.ctaCover)
        this.ctaBox = { x, y, w, h: BUTTON_H }
    }

    /** The button's box in panel space, following the section's glide. */
    private liveCtaBox = () => {
        if (!this.ctaBox || this.ctaTop === undefined) return undefined
        return { ...this.ctaBox, y: this.ctaBox.y + this.ctaTop + this.ctaSlide.value(this.clock) }
    }

    private pressCallToAction = () => {
        const cta = this.cta
        if (!cta || this.ctaSpent || ctaPose(this.clock - this.ctaShownAt).ignite < 1) return
        this.ctaPressedAt = this.clock
        if (cta.kind === "continue") {
            this.ctaSpent = true
            this.ctaSpentAt = this.clock
            this.ctaHover.set(0, this.clock)
            this.ctaButton?.setEnabled(false)
            if (!this.minimized) this.toggle()
            this.events.onContinue?.()
        } else {
            this.events.onNextChapter?.(cta.chapter)
        }
    }

    private paintBoard = (h: number) => {
        const g = this.board.clear()
        const w = WIDTH
        const complete = this.complete()
        g.beginFill(0x000000, 0.3)
        chamfer(g, 0, 3, w, h, 7)
        g.endFill()
        g.lineStyle({ width: 1.5, color: TIMBER_EDGE, alpha: 0.95, join: LINE_JOIN.MITER })
        g.beginFill(TIMBER, 0.95)
        chamfer(g, 0, 0, w, h, 7)
        g.endFill()
        const gold = this.headerGold.value(this.clock)
        g.lineStyle({
            width: 1,
            color: lerpColor(RIM, REWARD_GOLD_HOT, complete ? gold : 0),
            alpha: 0.8 + 0.15 * gold,
            join: LINE_JOIN.MITER,
        })
        g.beginFill(FACE, 0.9)
        chamfer(g, 4, 4, w - 8, Math.max(8, h - 8), 5)
        g.endFill()
        g.lineStyle(1, RIM_LIGHT, 0.16)
            .moveTo(11, 5.5)
            .lineTo(w - 11, 5.5)
        g.lineStyle(0)
        if (this.data && this.mode !== "compact") {
            // The header's gold spark, as on the tutorial board and the clues.
            g.beginFill(REWARD_GOLD, 1)
            spark(g, CONTENT_X + 4, 14, 4)
            g.endFill()
            if (this.rows.length && h > this.headerBottom + 2) groove(g, 10, w - 10, this.headerBottom)
            const ctaTop = this.ctaTop !== undefined ? this.ctaTop + this.ctaSlide.value(this.clock) : undefined
            if (ctaTop !== undefined && h > ctaTop + 2) groove(g, 10, w - 10, ctaTop + (this.rows.length ? 0 : 1))
        }
        if (h > 30 && this.mode !== "compact") {
            rivet(g, 10, h - 10)
            rivet(g, w - 10, h - 10)
        }
    }

    private paintSocket = (row: Row, checkDraw: number) => {
        const g = row.socket.clear()
        if (row.state === "done") {
            filledSocket(g, 0, 0, SOCKET_R, checkDraw)
            return
        }
        const glow = this.looks.get(row.task.id)?.glow.value(this.clock) ?? 0
        g.lineStyle({ width: 1, color: lerpColor(SOCKET_RIM_TODO, RIM_LIGHT, glow), alpha: 1, join: LINE_JOIN.MITER })
        g.beginFill(SOCKET_EMPTY, 1)
        diamond(g, 0, 0, SOCKET_R)
        g.endFill()
        g.lineStyle(0)
    }

    /** Frame-driven; deltaTime in ms. Repaints the live layers and settles anything in flight. */
    animate = (deltaTime: number) => {
        this.clock += Math.max(0, Math.min(100, deltaTime || 0))
        if (!this.visible) return
        this.settleClocks()
        if (!this.visible) return
        const height = this.boardHeight.value(this.clock)
        if (
            Math.abs(height - this.paintedHeight) > 0.01 ||
            !this.ctaSlide.settled(this.clock) ||
            !this.headerGold.settled(this.clock)
        ) {
            this.paintedHeight = height
            this.paintBoard(height)
        }
        // Clip the content to the board only while the board is moving; a stencil otherwise is waste.
        const folding = !this.boardHeight.settled(this.clock)
        this.contentMask.clear()
        if (folding) this.contentMask.beginFill(0xffffff, 1).drawRect(0, 0, WIDTH, height).endFill()
        this.content.mask = folding ? this.contentMask : null
        if (this.ctaLayer && this.ctaTop !== undefined) this.ctaLayer.y = this.ctaTop + this.ctaSlide.value(this.clock)
        // Folding to the bar fades everything out, then the bar's face fades in as the board shrinks.
        let contentAlpha =
            this.contentInAt === undefined ? 1 : easeOutCubic(span(this.clock - this.contentInAt, 0, CONTENT_FADE_MS))
        if (this.collapsingAt !== undefined && this.foldsToCompact()) {
            contentAlpha = Math.min(
                contentAlpha,
                1 - easeInCubic(span(this.clock - this.collapsingAt, 0, this.collapseMs())),
            )
        }
        this.content.alpha = contentAlpha
        this.pips.alpha = contentAlpha
        if (this.contentInAt !== undefined && this.clock - this.contentInAt > CONTENT_FADE_MS)
            this.contentInAt = undefined

        const under = this.under.clear()
        const over = this.over.clear()
        this.paintHeader(over)
        this.paintToggle()
        const pulse = currentPulse(this.clock)
        this.rows.forEach(row => this.paintRow(row, pulse, under, over))
        this.stamps.forEach((startedAt, id) => {
            const pose = stampPose(this.clock - startedAt)
            if (pose.panelFlash > 0) this.paintRimGlow(over, pose.panelFlash * 0.7, height)
            if (pose.done) {
                this.stamps.delete(id)
                const row = this.rows.find(entry => entry.task.id === id)
                if (row) {
                    row.socket.scale.set(1)
                    this.paintSocket(row, 1)
                }
            }
        })
        if (this.dockFlashAt !== undefined) {
            const t = span(this.clock - this.dockFlashAt, 0, 900)
            this.paintRimGlow(over, 1 - easeOutCubic(t), height)
            glowDisc(under, WIDTH / 2, height / 2, WIDTH * 0.7, REWARD_GOLD, 0.35 * (1 - t), 5)
            if (t >= 1) this.dockFlashAt = undefined
        }
        this.paintCallToAction(under, over)
    }

    private paintHeader = (over: Graphics) => {
        const gold = this.headerGold.value(this.clock)
        if (this.overline) this.overline.tint = lerpColor(RIM_LIGHT, REWARD_GOLD, gold)
        if (this.title) this.title.tint = lerpColor(INK, REWARD_GOLD, gold)
        const g = this.pips.clear()
        if (!this.data || this.mode === "compact") return
        // The pips give way to the COMPLETE chip, which lights up in their place.
        const sinceComplete = this.completeAt === undefined ? -1 : this.clock - this.completeAt
        const pipsAlpha = sinceComplete < 0 ? 1 : 1 - easeOutCubic(span(sinceComplete, 0, 260))
        if (pipsAlpha > 0) {
            this.data.tasks.forEach((task, index) => {
                const { x, y } = this.pipCenter(index)
                const stampedAt = this.stamps.get(task.id)
                if (this.shown(task)) {
                    const pop =
                        stampedAt === undefined
                            ? 1
                            : lerp(2.2, 1, easeOutBack(span(this.clock - stampedAt, 0, 380), 2.4))
                    if (stampedAt !== undefined) glowDisc(over, x, y, 9 * pop, REWARD_GOLD, 0.6 * (2.2 - pop), 3)
                    g.lineStyle(0).beginFill(REWARD_GOLD_HOT, pipsAlpha)
                    diamond(g, x, y, PIP_R * pop)
                    g.endFill()
                    g.beginFill(REWARD_GOLD, pipsAlpha)
                    diamond(g, x, y - 0.6 * pop, PIP_R * 0.55 * pop)
                    g.endFill()
                } else {
                    g.lineStyle(1, SOCKET_RIM_TODO, pipsAlpha).beginFill(SOCKET_EMPTY, pipsAlpha)
                    diamond(g, x, y, PIP_R)
                    g.endFill()
                    g.lineStyle(0)
                }
            })
        }
        if (this.chip && this.chipBox) {
            const t = sinceComplete < 0 ? 1 : span(sinceComplete, 180, 560)
            const scale = sinceComplete < 0 ? 1 : lerp(1.8, 1, easeOutBack(t, 2.4))
            const alpha = sinceComplete < 0 ? 1 : easeOutCubic(span(sinceComplete, 180, 300))
            const { x, w } = this.chipBox
            const cx = x + w / 2,
                cy = 14.5
            g.lineStyle({ width: 1, color: 0x6b4a14, alpha, join: LINE_JOIN.MITER })
            g.beginFill(REWARD_GOLD, alpha)
            chamfer(g, cx - (w / 2) * scale, cy - 6.5 * scale, w * scale, 13 * scale, 3 * scale)
            g.endFill()
            g.lineStyle(0)
            this.chip.scale.set(scale)
            this.chip.alpha = alpha
            if (sinceComplete >= 0 && sinceComplete < 1400) {
                glowDisc(over, cx, cy, w * 0.8, REWARD_GOLD, 0.5 * (1 - span(sinceComplete, 180, 1100)), 4)
                sparkBurst(over, cx, cy, span(sinceComplete, 200, 760), 10, w / 2, w / 2 + 22, REWARD_GOLD_HOT, 0.4)
                shineBand(over, x, cy - 6.5, w, 13, span(sinceComplete, 560, 1100), 16, 0.6)
            }
        }
    }

    private paintToggle = () => {
        const g = this.toggleGlyph.clear()
        if (!this.toggleButton) return
        // Pointing down while open; turns to point right as the list folds away.
        g.rotation = (-Math.PI / 2) * this.toggleTurn.value(this.clock)
        g.lineStyle({ width: 1.6, color: RIM_LIGHT, alpha: 0.9, join: LINE_JOIN.MITER })
        g.moveTo(-3.5, -1.5).lineTo(0, 2).lineTo(3.5, -1.5)
        g.lineStyle(0)
    }

    private paintRow = (row: Row, pulse: number, under: Graphics, over: Graphics) => {
        const look = this.looks.get(row.task.id)
        const n = this.rows.length
        // Cascading in on expand (and on first sight); fading out, last row first, on collapse.
        let alpha =
            this.rowsInAt === undefined
                ? 1
                : easeOutCubic(
                      span(
                          this.clock - this.rowsInAt,
                          row.index * ROW_IN_STAGGER,
                          row.index * ROW_IN_STAGGER + ROW_IN_MS,
                      ),
                  )
        if (this.collapsingAt !== undefined) {
            const from = (n - 1 - row.index) * ROW_OUT_STAGGER
            alpha = Math.min(alpha, 1 - easeInCubic(span(this.clock - this.collapsingAt, from, from + ROW_OUT_MS)))
        }
        const slide = -8 * (1 - alpha)
        row.label.alpha = alpha
        row.socket.alpha = alpha
        row.label.x = LABEL_X + slide
        row.socket.x = SOCKET_X + slide
        const cx = SOCKET_X + slide,
            cy = row.top + row.height / 2
        const glow = (look?.glow.value(this.clock) ?? 0) * alpha
        let tint = look?.tint.value(this.clock) ?? rowTint(row.state)
        // The objective to do next glows softly: a warm band across its row, an ember breathing in its
        // socket, and its label warming and cooling with the breath. Eased in and out, so finishing a
        // task hands the glow down the list instead of switching it.
        if (glow > 0.01) {
            // Strongest at the socket, fading out across the row in fine steps (coarse ones show).
            const steps = 16,
                stepW = (WIDTH - 12) / steps
            for (let i = 0; i < steps; i++) {
                const k = 1 - i / steps
                under
                    .beginFill(REWARD_GOLD, (0.04 + 0.02 * pulse) * k * k * glow)
                    .drawRect(6 + i * stepW, row.top + 1, stepW + 0.5, row.height - 2)
                    .endFill()
            }
            glowDisc(over, cx, cy, 8 + pulse * 2.5, REWARD_EMBER, (0.2 + 0.18 * pulse) * glow)
            over.beginFill(REWARD_GOLD_HOT, (0.45 + 0.4 * pulse) * glow)
                .drawCircle(cx, cy, 1.5 + pulse * 0.5)
                .endFill()
            if (row.state === "current") tint = lerpColor(tint, CURRENT_WARM, 0.35 * pulse * glow)
        }
        if (row.state !== "done" && look && !look.glow.settled(this.clock)) this.paintSocket(row, 1)
        const stampedAt = this.stamps.get(row.task.id)
        if (stampedAt !== undefined) {
            const pose = stampPose(this.clock - stampedAt)
            row.socket.scale.set(pose.socketScale)
            this.paintSocket(row, pose.checkDraw)
            tint = lerpColor(DONE_TEXT, REWARD_GOLD, pose.labelGold)
            glowDisc(under, cx, cy, 18 * (1 - pose.ringT * 0.5), REWARD_GOLD, 0.9 * (1 - pose.ringT))
            if (pose.ringT < 1) {
                over.lineStyle({
                    width: 2 * (1 - pose.ringT) + 0.5,
                    color: REWARD_GOLD,
                    alpha: 1 - pose.ringT,
                    join: LINE_JOIN.MITER,
                })
                diamond(over, cx, cy, lerp(SOCKET_R, 24, easeOutCubic(pose.ringT)))
                over.lineStyle(0)
            }
            sparkBurst(over, cx, cy, pose.sparkT, 10, 7, 26, REWARD_GOLD_HOT, row.index)
            if (pose.sweepT > 0 && pose.sweepT < 1)
                shineBand(over, 6, row.top, WIDTH - 12, row.height, pose.sweepT, 46, 0.32, REWARD_GOLD)
        }
        row.label.tint = tint
    }

    private settleClocks = () => {
        let changed = false
        this.pending.forEach((since, id) => {
            if (this.clock - since > PENDING_REVEAL_LIMIT_MS) {
                this.pending.delete(id)
                this.stamps.set(id, this.clock)
                changed = true
            }
        })
        if (this.awaitingDockSince !== undefined && this.clock - this.awaitingDockSince > DOCK_LIMIT_MS) {
            this.awaitingDockSince = undefined
            this.ctaShownAt = this.clock
            changed = true
        }
        if (this.ctaHeldUntil !== undefined && this.clock >= this.ctaHeldUntil) {
            this.ctaHeldUntil = undefined
            this.ctaShownAt = this.clock
            changed = true
        }
        if (this.ctaRetiringAt !== undefined && this.clock - this.ctaRetiringAt > RETIRE_MS) {
            this.ctaRetiringAt = undefined
            this.cta = undefined
            this.ctaSpent = false
            this.refreshVisibility()
            changed = true
        }
        if (this.collapsingAt !== undefined && this.clock - this.collapsingAt >= this.collapseMs()) {
            this.collapsingAt = undefined
            this.rowsInAt = undefined
            this.minimized = true
            changed = true
        }
        if (this.rowsInAt !== undefined && this.clock - this.rowsInAt > ROW_IN_MS + ROW_IN_STAGGER * this.rows.length) {
            this.rowsInAt = undefined
        }
        if (changed) {
            this.layout()
            this.place()
        }
    }

    private collapseMs = () => ROW_OUT_MS + ROW_OUT_STAGGER * Math.max(0, this.rows.length - 1)

    private paintRimGlow = (g: Graphics, strength: number, height: number) => {
        if (!(strength > 0)) return
        for (let i = 0; i < 3; i++) {
            g.lineStyle({
                width: 2 + i * 3,
                color: REWARD_GOLD,
                alpha: strength * (0.5 - i * 0.14),
                join: LINE_JOIN.MITER,
            })
            chamfer(g, 1, 1, WIDTH - 2, height - 2, 7)
        }
        g.lineStyle(0)
    }

    private paintCallToAction = (under: Graphics, over: Graphics) => {
        const cover = this.ctaCover.clear()
        const box = this.liveCtaBox(),
            button = this.ctaButton
        if (!box || !button || !this.ctaBox) return
        const pose = ctaPose(this.clock - this.ctaShownAt)
        const hover = this.ctaHover.value(this.clock)
        const spent = this.ctaSpent ? easeOutCubic(span(this.clock - this.ctaSpentAt, 0, 380)) : 0
        if (this.ctaLayer) {
            this.ctaLayer.alpha = pose.plateAlpha
            this.ctaLayer.y += pose.rise
        }
        const local = this.ctaBox
        button.scale.set(1 + 0.03 * hover * (1 - spent))
        button.alpha = 1 - 0.45 * spent
        // The unlit plate, drawn over the lit button and drawn back from the left as the light comes.
        if (pose.ignite < 1) {
            const front = local.w * pose.ignite
            cover.lineStyle({ width: 1, color: UNLIT_RIM, alpha: 1, join: LINE_JOIN.MITER })
            cover.beginFill(UNLIT_FILL, 1)
            const left = local.x + front
            const c = Math.min(5, local.h / 4)
            if (front <= c) chamfer(cover, local.x, local.y, local.w, local.h, c)
            else {
                cover
                    .moveTo(left, local.y)
                    .lineTo(local.x + local.w - c, local.y)
                    .lineTo(local.x + local.w, local.y + c)
                    .lineTo(local.x + local.w, local.y + local.h - c)
                    .lineTo(local.x + local.w - c, local.y + local.h)
                    .lineTo(left, local.y + local.h)
                    .closePath()
            }
            cover.endFill()
            cover.lineStyle(0)
        }
        const by = box.y + pose.rise
        if (pose.edge > 0) {
            const x = box.x + box.w * pose.ignite
            for (let i = 0; i < 3; i++) {
                over.beginFill(i === 0 ? REWARD_WHITE : REWARD_GOLD, pose.edge * (0.55 - i * 0.17))
                    .drawRect(x - 1 - i * 2, by - i * 1.5, 2 + i * 4, box.h + i * 3)
                    .endFill()
            }
            glowDisc(over, x, by + box.h / 2, 16, REWARD_GOLD, 0.35 * pose.edge, 3)
        }
        const glow = (0.6 * pose.glow + 0.3 * hover) * (1 - spent)
        const cx = box.x + box.w / 2,
            cy = by + box.h / 2
        if (glow > 0.01) {
            for (let i = 0; i < 4; i++) {
                const grow = 2 + i * 3
                under.lineStyle({
                    width: 3 + i * 2,
                    color: i < 2 ? REWARD_GOLD : REWARD_EMBER,
                    alpha: glow * (0.34 - i * 0.07),
                    join: LINE_JOIN.MITER,
                })
                chamfer(under, cx - box.w / 2 - grow, cy - box.h / 2 - grow, box.w + grow * 2, box.h + grow * 2, 6)
            }
            under.lineStyle(0)
        }
        if (pose.shineT >= 0 && !this.ctaSpent)
            shineBand(over, box.x + 2, by + 2, box.w - 4, box.h - 4, pose.shineT, 30, 0.22)
        if (hover > 0.01 && !this.ctaSpent) {
            over.beginFill(REWARD_GOLD, 0.06 * hover)
            chamfer(over, box.x + 1, by + 1, box.w - 2, box.h - 2, 4)
            over.endFill()
        }
        // The press answers: a flash through the plate and a ring of sparks off it.
        if (this.ctaPressedAt !== undefined) {
            const t = span(this.clock - this.ctaPressedAt, 0, 520)
            if (t < 1) {
                over.beginFill(REWARD_GOLD, 0.35 * (1 - easeOutCubic(t)))
                chamfer(over, box.x, by, box.w, box.h, 5)
                over.endFill()
                sparkBurst(over, cx, cy, t, 12, box.h / 2, box.h / 2 + 22, REWARD_GOLD_HOT, 0.1, 1.5)
            } else {
                this.ctaPressedAt = undefined
            }
        }
    }

    private startDrag = (event: InteractionEvent) => {
        if (this.drag || !this.parent || (event.data.button ?? 0) !== 0) return
        event.stopPropagation()
        const at = this.parent.toLocal(event.data.global)
        let interaction: InteractionManager | undefined
        try {
            interaction = getRendererApp().renderer.plugins.interaction
        } catch {
            /* Standalone previews use header events. */
        }
        this.drag = { id: event.data.pointerId, offset: new Point(at.x - this.x, at.y - this.y), interaction }
        this.manualPosition = this.position.clone()
        this.headerHandle.cursor = "grabbing"
        this.hovered = true
        this.hover(true)
        interaction?.on("pointermove", this.moveDrag)
        interaction?.on("pointerup", this.endDrag)
        interaction?.on("pointerupoutside", this.endDrag)
        interaction?.on("pointercancel", this.endDrag)
    }

    private moveDrag = (event: InteractionEvent) => {
        if (!this.drag || !this.parent || event.data.pointerId !== this.drag.id) return
        event.stopPropagation()
        const at = this.parent.toLocal(event.data.global)
        this.manualPosition.set(at.x - this.drag.offset.x, at.y - this.drag.offset.y)
        this.place()
    }

    private endDrag = (event?: InteractionEvent) => {
        if (!this.drag || (event && event.data.pointerId !== this.drag.id)) return
        const interaction = this.drag.interaction
        interaction?.off("pointermove", this.moveDrag)
        interaction?.off("pointerup", this.endDrag)
        interaction?.off("pointerupoutside", this.endDrag)
        interaction?.off("pointercancel", this.endDrag)
        this.drag = undefined
        if (this.headerHandle && !this.headerHandle.destroyed) this.headerHandle.cursor = "grab"
        this.hovered = !!event?.data?.global && this.getBounds().contains(event.data.global.x, event.data.global.y)
        this.hover(this.hovered)
    }

    place = () => {
        if (!this.visible) return
        const safe = getUiMetrics().safeViewport
        const desired = this.manualPosition || this.anchor() || { x: safe.left + 12, y: safe.top + 64 }
        const fit = Math.max(0.2, Math.min(1, (safe.width - 24) / WIDTH, (safe.height - 24) / this.panelHeight))
        this.scale.set(fit)
        const x = Math.max(safe.left, Math.min(desired.x, Math.max(safe.left, safe.right - WIDTH * fit)))
        const y = Math.max(safe.top, Math.min(desired.y, Math.max(safe.top, safe.bottom - this.panelHeight * fit)))
        this.position.set(x, y)
        this.manualPosition?.set(x, y)
    }

    destroy = () => {
        this.endDrag()
        if (this.hovered) this.hover(false)
        for (const keep of [this.ctaCover, this.toggleGlyph]) keep.parent?.removeChild(keep)
        this.ctaCover.destroy()
        this.toggleGlyph.destroy()
        super.destroy({ children: true })
    }
}
